import { Component, Injector, Input, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { ApAlertService, reportErrorToUserAndServer } from 'webcommon/core-ui';
import { FcFilterTypeModalBaseComponent, } from '../../ap-findcontrol';
import { ListFilterType } from '../../ap-findcontrol/ListFilterType';
import { FcListValidationService } from '../../ap-findcontrol/generic/fc-list-validation/fc-list-validation.service';
import {
  FindControlRequiredSearchParametersDto,
  GuidStrValue,
  EventComposite,
  FindControlAdditionalSearchParametersDto,
  FindControlAdditionalSearchParametersType,
  RegexUtilityService,
  VisibleToEnum,
  YesNoBoth,
} from 'webcommon/shared';
import { ListFilterRepository } from 'webcommon/webapi';
import { tap, switchMap } from 'rxjs/operators';
import { of, EMPTY } from 'rxjs';
import { FilterSubmitType } from '../../models';
import { TrackPatientResultFilterFormType, TrackPatientResultsFilterFormControls } from '../../models/TrackPatientResultFilterFormType';
import { TrackPatientResultFilterUtilityService } from './track-patient-result-filter-utility.service';

@Component({
  selector: 'ap-result-status-filter-modal',
  templateUrl: './result-status-filter-modal.component.html',
  providers: [ApAlertService, FcListValidationService]
})
export class ResultStatusFilterModalComponent
  extends FcFilterTypeModalBaseComponent<ListFilterType.ControlResultTrackingFilter>
  implements OnInit {

  @Input() timespanFindControlSearchParams: FindControlAdditionalSearchParametersDto;

  title = 'Result Status/Result';
  baseId = 'resultStatusFilter';

  isFormSubmitted = false;

  readonly FilterSubmitType = FilterSubmitType;

  private filterName = '';
  private filterExternalId: string | null = null;
  private filterNotes = '';
  orderDateSelectedPks: GuidStrValue[] = [];
  dueDateSelectedPks: GuidStrValue[] = [];
  resultDateSelectedPks: GuidStrValue[] = [];
  activeFilter$ = new EventComposite(this, (filterId: GuidStrValue | null) => {
    if (!!filterId) {
      return this.listFilterRepository.getTrackPatientResultsFilter(filterId);
    } else {
      return of(null);
    }
  }).start(null);


  resultSearchParameters: FindControlRequiredSearchParametersDto = {
    RequiredSearchParameters: [{ ColumnName: 'ListType', Value: 'AbnormalFlagCode', Negate: false }]
  };

  templateSearchParameters: FindControlAdditionalSearchParametersDto = {
    Type: FindControlAdditionalSearchParametersType.Standard,
    AdditionalSearchParameters: [{ ColumnName: 'ObservationTypeID', Value: '2', Negate: false }]
  };

  providerSearchParameters: FindControlAdditionalSearchParametersDto = {
    Type: FindControlAdditionalSearchParametersType.Standard,
    AdditionalSearchParameters: [{ ColumnName: 'IsActiveUser', Value: 'true', Negate: false }]
  };

  referringProviderSearchParameters: FindControlAdditionalSearchParametersDto = {
    Type: FindControlAdditionalSearchParametersType.Standard,
    AdditionalSearchParameters: [{ ColumnName: 'IsActiveUser', Value: 'true', Negate: false }]
  };

  serviceSiteSearchParameters: FindControlAdditionalSearchParametersDto = {
    Type: FindControlAdditionalSearchParametersType.Standard,
    AdditionalSearchParameters: [{ ColumnName: 'InternalSite', Value: 'true', Negate: false }],
  };

  orderDateSearchParameters: FindControlRequiredSearchParametersDto = {
    RequiredSearchParameters: [{ ColumnName: '', Value: 'ResultTrackingOrderDateType', Negate: false }]
  };

  dueDateSearchParameters: FindControlRequiredSearchParametersDto = {
    RequiredSearchParameters: [{ ColumnName: '', Value: 'ResultTrackingDueDateType', Negate: false }]
  };

  resultDateSearchParameters: FindControlRequiredSearchParametersDto = {
    RequiredSearchParameters: [{ ColumnName: '', Value: 'ResultTrackingResultDateType', Negate: false }]
  };

  trackPatientResultsFilterForm = new FormGroup<TrackPatientResultFilterFormType>({
    Status: new FormControl<GuidStrValue[] | null>(null),
    Laboratory: new FormControl<GuidStrValue[] | null>(null),
    Results: new FormControl<GuidStrValue[] | null>(null),
    AccessionNumber: new FormControl<string | null>(null, [
      Validators.maxLength(25),
      Validators.pattern(
        RegexUtilityService.CharsNumbs
      )
    ]),
    Patient: new FormControl<GuidStrValue[] | null>(null),
    PatientCity: new FormControl<string | null>({ value: null, disabled: true }),
    PatientState: new FormControl<string | null>({ value: null, disabled: true }),
    PatientZip: new FormControl<string | null>({ value: null, disabled: true }),

    Urgency: new FormControl<GuidStrValue[] | null>(null),
    Template: new FormControl<GuidStrValue[] | null>(null),
    AttachmentType: new FormControl<GuidStrValue[] | null>(null),

    OrderDate: new FormControl<GuidStrValue | null>(null),
    DueDate: new FormControl<GuidStrValue | null>(null),
    ResultDate: new FormControl<GuidStrValue | null>(null),

    Provider: new FormControl<GuidStrValue[] | null>(null),
    ReferringProvider: new FormControl<GuidStrValue[] | null>(null),
    ServiceSite: new FormControl<GuidStrValue[] | null>(null),
    Procedure: new FormControl<GuidStrValue[] | null>(null),

    PSC: new FormControl<YesNoBoth | null>(YesNoBoth.Both),
    FutureOrders: new FormControl<YesNoBoth | null>(YesNoBoth.Both),

    MaximumItemsReturned: new FormControl<number | null>(100, [
      Validators.min(-1),
      Validators.max(10000)
    ]),

    IncludeStrikeouts: new FormControl<boolean | null>(false),
  });

  filterForm = new FormGroup<TrackPatientResultsFilterFormControls>({
    details: this.trackPatientResultsFilterForm,
    common: this.commonFilterForm
  });

  onOrderDateChanged(pks: GuidStrValue[] | null): void {
    const value = pks && pks.length > 0 ? pks[0] : null;
    this.trackPatientResultsFilterForm.controls.OrderDate.setValue(value);
  }


  onDueDateChanged(pks: GuidStrValue[] | null): void {
    const value = pks && pks.length > 0 ? pks[0] : null;
    this.trackPatientResultsFilterForm.controls.DueDate.setValue(value);
  }


  onResultDateChanged(pks: GuidStrValue[] | null): void {
    const value = pks && pks.length > 0 ? pks[0] : null;
    this.trackPatientResultsFilterForm.controls.ResultDate.setValue(value);
  }

  constructor(
    private readonly listFilterRepository: ListFilterRepository,
    private readonly filterUtilityService: TrackPatientResultFilterUtilityService,
    protected injector: Injector
  ) {
    super(injector);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.hideDefault = false;
    const patientId = this.getPatientId();
    if (patientId) this.initializePatient(patientId);

    if (!this.filterData?.filterId && this.filterData.data) {
      this.orderDateSelectedPks = this.filterData.data.OrderDateUid ? [this.filterData.data.OrderDateUid] : [];
      this.dueDateSelectedPks = this.filterData.data.DueDateUid ? [this.filterData.data.DueDateUid] : [];
      this.resultDateSelectedPks = this.filterData.data.ResultDateUid ? [this.filterData.data.ResultDateUid] : [];
      this.filterForm.patchValue(this.filterUtilityService.mapDtoToFormValue(this.filterData.data));
    }

    this.activeFilter$.afterLoad$
      .pipe(
        tap(filterData => {
          if (!filterData) {
            return;
          }
          this.filterName = filterData.Name;
          this.filterExternalId = filterData.ExternalID ?? null;
          this.filterNotes = filterData.Notes ?? '';
          this.updateTitle();

          this.orderDateSelectedPks = filterData.OrderDateUid ? [filterData.OrderDateUid] : [];
          this.dueDateSelectedPks = filterData.DueDateUid ? [filterData.DueDateUid] : [];
          this.resultDateSelectedPks = filterData.ResultDateUid ? [filterData.ResultDateUid] : [];
          this.filterForm.patchValue(this.filterUtilityService.mapDtoToFormValue(filterData));
        }),
        this.takeUntilDestroy()
      )
      .subscribe();
    this.activeFilter$.change$.next(this.filterData?.filterId ?? null);
  }

  private getPatientId(): GuidStrValue | null {
    return this.patientId ? this.patientId : null;
  }

  private initializePatient(patientId: GuidStrValue): void {

    // TO DO call the patient demographics details to get the city, state, zip.
    this.trackPatientResultsFilterForm.controls.Patient.setValue([patientId]);
    this.updateTitle();
  }

  private updateTitle(): void {
    this.title = this.filterName
      ? `Result Status/Result - ${this.filterName}`
      : 'Result Status/Result';
  }

  onSubmit(submitType: FilterSubmitType): void {
    if (submitType === FilterSubmitType.Clear) {
      this.clearFormValues();
      return;
    }

    this.isFormSubmitted = true;
    if (this.trackPatientResultsFilterForm.invalid) {
      this.trackPatientResultsFilterForm.markAllAsTouched();
      return;
    }

    if (this.trackPatientResultsFilterForm.valid) {
      switch (submitType) {
        case FilterSubmitType.Apply:
          this.applyFilterClick();
          break;
        case FilterSubmitType.Save:
          this.saveFilterClick();
          break;
        case FilterSubmitType.SaveAs:
          this.saveAsFilterClick();
          break;
        default:
          throw (new Error('Error Submitting Form'));
      }
    }
  }

  private applyFilterClick(): void {
    const dto = this.filterUtilityService.mapToResultStatusFilterApplyDto(
      this.filterForm.getRawValue(),
      this.getPatientId()
    );
    this.applyFilterClickDone({
      filterType: ListFilterType.ControlResultTrackingFilter,
      filterId: this.filterData?.filterId ?? null,
      data: dto
    });
  }

  private saveFilterClick(): void {
    if (!this.filterData.filterId) {
      this.saveAsFilterClick();
      return;
    }

    const dto = this.filterUtilityService.mapToResultStatusFilterDto(
      this.filterData.filterId, this.filterName, this.filterExternalId, this.filterForm.getRawValue(),
      this.getPatientId(), this.filterNotes
    );

    this.listFilterRepository.postTrackPatientResultsFilter(dto).pipe(
      reportErrorToUserAndServer(this.apNotificationService),
      this.takeUntilDestroy()
    ).subscribe((saved) => {
      this.filterName = saved.Name;
      this.filterExternalId = saved.ExternalID ?? null;
      this.updateTitle();
      this.saveFilterClickDone({
        filterType: ListFilterType.ControlResultTrackingFilter,
        filterId: saved.FilterUid ?? this.filterData.filterId ?? null,
        data: null,
      });
    });
  }

  private saveAsFilterClick(): void {
    this.saveAsFilterModal()
      .pipe(
        switchMap((saveAsResult) => {
          if (saveAsResult?.isSuccess) {
            const dto = this.filterUtilityService.mapToResultStatusFilterDto(
              null, saveAsResult.resultValue.Name, saveAsResult.resultValue.ID, this.filterForm.getRawValue(),
              this.getPatientId(), this.filterNotes
            );
            return this.listFilterRepository.postTrackPatientResultsFilterSaveAs(dto);
          }
          return EMPTY;
        })
      ).pipe(
        reportErrorToUserAndServer(this.apNotificationService),
        this.takeUntilDestroy()
      )
      .subscribe((result) => {
        this.filterName = result.Name;
        this.filterExternalId = result.ExternalID ?? null;
        this.updateTitle();
        this.saveAsFilterClickDone({
          filterType: ListFilterType.ControlResultTrackingFilter,
          filterId: result.FilterUid ?? null,
          data: null,
        });
      });
  }


  public clearFormValues(): void {
    this.activeFilter$.change$.next(null);
    this.filterName = '';
    this.filterExternalId = null;
    this.filterNotes = '';
    this.updateTitle();

    this.trackPatientResultsFilterForm.reset({
      PSC: YesNoBoth.Both,
      FutureOrders: YesNoBoth.Both,
      MaximumItemsReturned: 100,
      IncludeStrikeouts: false,
      Status: [],
      Laboratory: [],
      Results: [],
      Urgency: [],
      Template: [],
      AttachmentType: [],
      Provider: [],
      ReferringProvider: [],
      ServiceSite: [],
      Procedure: [],
      Patient: null,
      OrderDate: null,
      DueDate: null,
      ResultDate: null,
      AccessionNumber: null,
    });

    this.orderDateSelectedPks = [];
    this.dueDateSelectedPks = [];
    this.resultDateSelectedPks = [];

    this.commonFilterForm.reset({
      visibleTo: VisibleToEnum.Me,
      makeMyDefault: false,
    });

    const patientId = this.getPatientId();
    if (patientId) {
      this.initializePatient(patientId);
    }

    this.isFormSubmitted = false;
  }

  cancel(): void {
    this.cancelFilterClick();
  }

  showErrors(control: AbstractControl, isFormSubmitted = false): boolean {
    return FcListValidationService.showErrors(control, isFormSubmitted);
  }
}
